// src/pages/workflow/WorkflowEditPage.tsx
// n8n-like visual pipeline editor with horizontal left-to-right flow,
// drag-to-connect ports, smooth bezier connections, and context menus.

import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useWorkflow, useI18n, useAuth } from '@react/hooks';
import { authzApi, workflowApi } from '@core/api';
import { useScope } from '@/hooks/useScope';
import {
  Button,
  Badge,
  Input,
  Textarea,
  ErrorAlert,
  Alert,
} from '@react/components';
import {
  nodeTypeCategoryMap,
  getEntityId,
} from '@/features/workflow/logic';
import { DebugPanel } from '@/features/workflow/DebugPanel';
import { ConfigFieldEditor } from './ConfigFieldEditor';
import { Modal } from '@/features/workflow/Modal';
import { useFullPaneSurface } from '@/features/tabs/PaneSurface';
import { useTabId } from '@/components/tabs/TabContext';
import { entityStore } from '@/stores/EntityStore';

// --- Constants ---

const NODE_WIDTH = 220;
const NODE_HEIGHT = 72;
const PORT_RADIUS = 5;
const PORT_HIT_RADIUS = 12;
const MIN_ZOOM = 0.15;
const MAX_ZOOM = 2.5;
const GRID_SIZE = 20;
const NODE_GAP_X = 80;
const NODE_GAP_Y = 40;

// --- Types ---

interface EditorNode {
  id: string;
  name: string;
  node_type_slug: string;
  position_x: number;
  position_y: number;
  config: Record<string, any>;
  is_disabled: boolean;
  notes: string;
  credential_id: string;
  // UI state
  _selected?: boolean;
  _category?: string;
  _color?: string;
  _icon_url?: string;
}

function placeLoadedNodes(rawNodes: any[]): { nodes: EditorNode[]; adjusted: boolean } {
  const nodes = rawNodes.map((node) => ({
    ...node,
    // Proto JSON omits zero-valued coordinates. CSS cannot position an
    // absolutely positioned node whose left/top is undefined.
    position_x: Number.isFinite(Number(node.position_x)) ? Number(node.position_x) : 0,
    position_y: Number.isFinite(Number(node.position_y)) ? Number(node.position_y) : 0,
    _category: '',
    _color: '#6b7280',
    _selected: false,
  })) as EditorNode[];
  const columns = [...new Set(nodes.map((node) => node.position_x))].sort((a, b) => a - b);
  const columnX = new Map<number, number>();
  columns.forEach((x, index) => {
    const previous = index ? columnX.get(columns[index - 1])! + NODE_WIDTH + NODE_GAP_X : x;
    columnX.set(x, Math.max(x, previous));
  });
  let adjusted = false;
  for (const node of nodes) {
    const x = columnX.get(node.position_x)!;
    if (x !== node.position_x) adjusted = true;
    node.position_x = x;
  }
  for (const x of columnX.values()) {
    const column = nodes.filter((node) => node.position_x === x).sort((a, b) => a.position_y - b.position_y);
    for (let index = 1; index < column.length; index++) {
      const minY = column[index - 1].position_y + NODE_HEIGHT + NODE_GAP_Y;
      if (column[index].position_y < minY) {
        column[index].position_y = minY;
        adjusted = true;
      }
    }
  }
  return { nodes, adjusted };
}

function openNodePosition(nodes: EditorNode[], x: number, y: number): { x: number; y: number } {
  let candidateX = x;
  for (let attempt = 0; attempt <= nodes.length; attempt++) {
    const occupied = nodes.some((node) =>
      Math.abs(node.position_x - candidateX) < NODE_WIDTH + NODE_GAP_X &&
      Math.abs(node.position_y - y) < NODE_HEIGHT + NODE_GAP_Y);
    if (!occupied) return { x: candidateX, y };
    candidateX += NODE_WIDTH + NODE_GAP_X;
  }
  return { x: candidateX, y };
}

interface EditorConnection {
  id: string;
  source_node_id: string;
  source_port_id: string;
  target_node_id: string;
  target_port_id: string;
  condition: string;
  label: string;
}

interface CanvasState {
  zoom: number;
  offsetX: number;
  offsetY: number;
}

interface ContextMenu {
  x: number;
  y: number;
  nodeId: string;
}

interface DragConnection {
  sourceNodeId: string;
  mouseX: number;
  mouseY: number;
}

// --- Helpers ---

function generateId(): string {
  return 'node_' + Math.random().toString(36).slice(2, 10);
}

function generateConnectionId(): string {
  return 'conn_' + Math.random().toString(36).slice(2, 10);
}

function getConnectionPath(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
): string {
  const dx = Math.abs(targetX - sourceX);
  const cpOffset = Math.max(dx * 0.4, 80);
  return `M ${sourceX} ${sourceY} C ${sourceX + cpOffset} ${sourceY}, ${targetX - cpOffset} ${targetY}, ${targetX} ${targetY}`;
}

function getConnectionMidpoint(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
): { x: number; y: number } {
  // Approximate midpoint of the bezier
  return {
    x: (sourceX + targetX) / 2,
    y: (sourceY + targetY) / 2,
  };
}

function getCategoryColor(category: string | undefined, fallback?: string): string {
  if (!category) return fallback || '#6b7280';
  return nodeTypeCategoryMap[category]?.color || fallback || '#6b7280';
}

// --- Icons (inline SVG) ---

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function SaveIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  );
}

function ZoomInIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
      <line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
    </svg>
  );
}

function ZoomOutIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
      <line x1="8" y1="11" x2="14" y2="11" />
    </svg>
  );
}

function FitIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}

function DuplicateIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V6a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2h2" />
    </svg>
  );
}

function NoteIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

function ToggleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="5" width="22" height="14" rx="7" ry="7" /><circle cx="16" cy="12" r="3" />
    </svg>
  );
}

function PanelLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="3" x2="9" y2="21" />
    </svg>
  );
}

function KeyboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M6 8h.01M10 8h.01M14 8h.01M18 8h.01M8 12h.01M12 12h.01M16 12h.01M7 16h10" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

// --- Main Component ---

export const WorkflowEditPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useWorkflow();
  const auth = useAuth();
  const tabId = useTabId();
  const { t } = useI18n();
  const scope = useScope();
  // A canvas with its own toolbar and inspector: it needs the pane's rectangle,
  // which is what the -m-6 used to be fighting the pane for.
  const paneSurface = useFullPaneSurface();

  // --- State ---
  const [pipeline, setPipeline] = useState<any>(null);
  const [graphLoading, setGraphLoading] = useState(true);
  const [graphError, setGraphError] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [accessReady, setAccessReady] = useState(false);
  const [nodes, setNodes] = useState<EditorNode[]>([]);
  const [connections, setConnections] = useState<EditorConnection[]>([]);
  const [canvas, setCanvas] = useState<CanvasState>({ zoom: 1, offsetX: 0, offsetY: 0 });
  const [nodeTypes, setNodeTypes] = useState<any[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragNodeId, setDragNodeId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [showNodePanel, setShowNodePanel] = useState(true);
  const [nodeTypeSearch, setNodeTypeSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [dragConnection, setDragConnection] = useState<DragConnection | null>(null);
  const [hoveredPortNodeId, setHoveredPortNodeId] = useState<string | null>(null);
  const [hoveredPortType, setHoveredPortType] = useState<'input' | 'output' | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenu | null>(null);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [quickSearch, setQuickSearch] = useState<{ x: number; y: number } | null>(null);
  const [quickSearchQuery, setQuickSearchQuery] = useState('');
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  // EVENT trigger config — pick the Kafka event this pipeline reacts to.
  // Pipeline.trigger_event/trigger_filter live on the pipeline record itself
  // (not the draft version), so this saves via UpdatePipeline, not SaveDraft.
  const [eventBrowserOpen, setEventBrowserOpen] = useState(false);
  const [eventDescriptors, setEventDescriptors] = useState<any[]>([]);
  const [eventDescriptorsLoaded, setEventDescriptorsLoaded] = useState(false);
  const [selectedEventName, setSelectedEventName] = useState('');
  const [selectedEventDetail, setSelectedEventDetail] = useState<any>(null);
  const [savingTriggerEvent, setSavingTriggerEvent] = useState(false);

  const canvasRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const quickSearchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!id || !pipeline?.name) return;
    entityStore.loadEntity(tabId, { urn: '', type: 'pipeline', id, title: pipeline.name });
    return () => entityStore.clearContext(tabId);
  }, [tabId, id, pipeline?.name]);

  useEffect(() => {
    if (!id || !auth.activeProfileId) return;
    let cancelled = false;
    setAccessReady(false);
    authzApi.Check({ user: `profile:${auth.activeProfileId}`, relation: 'owner', object: `pipeline:${id}`, authorization_model_id: '' })
      .then((response) => { if (!cancelled) setCanEdit(response.allowed); })
      .catch(() => { if (!cancelled) setCanEdit(false); })
      .finally(() => { if (!cancelled) setAccessReady(true); });
    return () => { cancelled = true; };
  }, [id, auth.activeProfileId]);

  // --- Load pipeline and version data ---
  useEffect(() => {
    if (!id) return;
    setGraphLoading(true);
    setGraphError(false);
    store.GetPipeline?.(id)?.then((p: any) => {
      const data = p?.pipeline ?? p;
      if (data) {
        setPipeline(data);
        void (async () => {
          const items: any[] = [];
          const seenCursors = new Set<string>();
          let cursor = '';
          do {
            const res = await workflowApi.ListNodeTypes({
              product_slug: data.product_slug || 'workflow',
              pagination: { pagination: { limit: 100, cursor, direction: 'forward' } },
            });
            items.push(...(res.items?.items ?? []));
            const next = res.pagination?.page_info?.has_next ? res.pagination.page_info.next_cursor : '';
            if (!next || seenCursors.has(next)) break;
            seenCursors.add(next);
            cursor = next;
          } while (cursor);
          setNodeTypes(items);
        })().catch(() => {});
        const versionId = data.draft_version_id || data.current_version_id;
        if (versionId) {
          store.GetPipelineVersion?.(versionId)?.then((response: any) => {
            const v = response?.version ?? response;
            if (v) {
              const { nodes: loadedNodes, adjusted } = placeLoadedNodes(v.nodes || []);
              setNodes(loadedNodes);
              setConnections(v.connections || []);
              if (adjusted) setHasChanges(true);
              if (v.canvas_settings?.offset_x != null && v.canvas_settings?.offset_y != null && !adjusted) {
                setCanvas({
                  zoom: v.canvas_settings.zoom || 1,
                  offsetX: v.canvas_settings.offset_x,
                  offsetY: v.canvas_settings.offset_y,
                });
              } else if (loadedNodes.length > 0) {
                // Missing offsets represent the default viewport, which may
                // leave negative coordinates outside the visible canvas.
                requestAnimationFrame(() => fitToView(loadedNodes));
              }
            }
          }).catch(() => setGraphError(true)).finally(() => setGraphLoading(false));
        } else {
          setGraphLoading(false);
        }
      } else {
        setGraphLoading(false);
      }
    }).catch(() => { setGraphError(true); setGraphLoading(false); });

  }, [id]);

  // Enrich node colors once nodeTypes are loaded
  useEffect(() => {
    if (nodeTypes.length === 0 || nodes.length === 0) return;
    setNodes((prev) =>
      prev.map((n) => {
        const nt = nodeTypes.find((t: any) => t.slug === n.node_type_slug);
        if (!nt) return n;
        return {
          ...n,
          _category: nt.category || n._category,
          _color: nt.color || getCategoryColor(nt.category) || n._color,
          _icon_url: nt.icon_url || n._icon_url,
        };
      }),
    );
  }, [nodeTypes]);

  // --- Derived state ---

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId) || null,
    [nodes, selectedNodeId],
  );

  const filteredNodeTypes = useMemo(() => {
    if (!nodeTypeSearch) return nodeTypes;
    const q = nodeTypeSearch.toLowerCase();
    return nodeTypes.filter(
      (nt: any) =>
        (nt.name || '').toLowerCase().includes(q) ||
        (nt.slug || '').toLowerCase().includes(q) ||
        (nt.category || '').toLowerCase().includes(q),
    );
  }, [nodeTypes, nodeTypeSearch]);

  const groupedNodeTypes = useMemo(() => {
    const groups: Record<string, any[]> = {};
    for (const nt of filteredNodeTypes) {
      const cat = nt.category || 'NODE_TYPE_CATEGORY_UTILITY';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(nt);
    }
    return groups;
  }, [filteredNodeTypes]);

  const quickSearchFiltered = useMemo(() => {
    if (!quickSearchQuery) return nodeTypes.slice(0, 12);
    const q = quickSearchQuery.toLowerCase();
    return nodeTypes
      .filter(
        (nt: any) =>
          (nt.name || '').toLowerCase().includes(q) ||
          (nt.slug || '').toLowerCase().includes(q),
      )
      .slice(0, 12);
  }, [nodeTypes, quickSearchQuery]);

  // --- Canvas coordinate conversion ---

  const screenToCanvas = useCallback(
    (screenX: number, screenY: number) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return { x: screenX, y: screenY };
      return {
        x: (screenX - rect.left - canvas.offsetX) / canvas.zoom,
        y: (screenY - rect.top - canvas.offsetY) / canvas.zoom,
      };
    },
    [canvas],
  );

  // --- Port positions ---

  const getOutputPortPos = useCallback(
    (node: EditorNode) => ({
      x: node.position_x + NODE_WIDTH,
      y: node.position_y + NODE_HEIGHT / 2,
    }),
    [],
  );

  const getInputPortPos = useCallback(
    (node: EditorNode) => ({
      x: node.position_x,
      y: node.position_y + NODE_HEIGHT / 2,
    }),
    [],
  );

  // --- Fit to view ---

  const fitToView = useCallback(
    (targetNodes?: EditorNode[]) => {
      const ns = targetNodes || nodes;
      if (ns.length === 0) return;
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const padding = 80;
      const minX = Math.min(...ns.map((n) => n.position_x));
      const maxX = Math.max(...ns.map((n) => n.position_x + NODE_WIDTH));
      const minY = Math.min(...ns.map((n) => n.position_y));
      const maxY = Math.max(...ns.map((n) => n.position_y + NODE_HEIGHT));

      const contentW = maxX - minX + padding * 2;
      const contentH = maxY - minY + padding * 2;
      const zoom = Math.min(
        Math.min(rect.width / contentW, rect.height / contentH),
        1.2,
      );
      const clampedZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom));

      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;
      setCanvas({
        zoom: clampedZoom,
        offsetX: rect.width / 2 - centerX * clampedZoom,
        offsetY: rect.height / 2 - centerY * clampedZoom,
      });
    },
    [nodes],
  );

  // --- Canvas mouse handlers ---

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    // Close context menu on any click
    if (contextMenu) {
      setContextMenu(null);
      return;
    }
    // Close quick search
    if (quickSearch) {
      setQuickSearch(null);
      setQuickSearchQuery('');
      return;
    }

    const target = e.target as HTMLElement;
    const isCanvasBg =
      target === canvasRef.current ||
      target === (svgRef.current as unknown as HTMLElement) ||
      target.classList.contains('canvas-grid');

    if (isCanvasBg) {
      setSelectedNodeId(null);
      setSelectedConnectionId(null);
      setIsPanning(true);
      setPanStart({ x: e.clientX - canvas.offsetX, y: e.clientY - canvas.offsetY });
    }
  };

  const handleCanvasMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      setCanvas((prev) => ({
        ...prev,
        offsetX: e.clientX - panStart.x,
        offsetY: e.clientY - panStart.y,
      }));
    }
    if (isDragging && dragNodeId) {
      const pos = screenToCanvas(e.clientX, e.clientY);
      setNodes((prev) =>
        prev.map((n) =>
          n.id === dragNodeId
            ? {
                ...n,
                position_x: Math.round((pos.x - dragOffset.x) / (GRID_SIZE / 2)) * (GRID_SIZE / 2),
                position_y: Math.round((pos.y - dragOffset.y) / (GRID_SIZE / 2)) * (GRID_SIZE / 2),
              }
            : n,
        ),
      );
      setHasChanges(true);
    }
    if (dragConnection) {
      const pos = screenToCanvas(e.clientX, e.clientY);
      setDragConnection((prev) =>
        prev ? { ...prev, mouseX: pos.x, mouseY: pos.y } : null,
      );
    }
  };

  const handleCanvasMouseUp = (e: React.MouseEvent) => {
    if (dragConnection) {
      // Check if we're over an input port of any node
      const pos = screenToCanvas(e.clientX, e.clientY);
      const targetNode = nodes.find((n) => {
        if (n.id === dragConnection.sourceNodeId) return false;
        const inputPort = getInputPortPos(n);
        const dx = pos.x - inputPort.x;
        const dy = pos.y - inputPort.y;
        return Math.sqrt(dx * dx + dy * dy) < PORT_HIT_RADIUS * 2;
      });
      if (targetNode) {
        const exists = connections.some(
          (c) =>
            c.source_node_id === dragConnection.sourceNodeId &&
            c.target_node_id === targetNode.id,
        );
        if (!exists) {
          const newConn: EditorConnection = {
            id: generateConnectionId(),
            source_node_id: dragConnection.sourceNodeId,
            source_port_id: 'output',
            target_node_id: targetNode.id,
            target_port_id: 'input',
            condition: '',
            label: '',
          };
          setConnections((prev) => [...prev, newConn]);
          setHasChanges(true);
        }
      }
      setDragConnection(null);
    }
    setIsPanning(false);
    setIsDragging(false);
    setDragNodeId(null);
  };

  const handleCanvasDoubleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const isCanvasBg =
      target === canvasRef.current ||
      target === (svgRef.current as unknown as HTMLElement) ||
      target.classList.contains('canvas-grid');
    if (!isCanvasBg) return;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    setQuickSearch({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setQuickSearchQuery('');
    setTimeout(() => quickSearchInputRef.current?.focus(), 50);
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const delta = e.deltaY > 0 ? -0.08 : 0.08;
      const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, canvas.zoom + delta));
      const ratio = newZoom / canvas.zoom;

      setCanvas({
        zoom: newZoom,
        offsetX: mouseX - (mouseX - canvas.offsetX) * ratio,
        offsetY: mouseY - (mouseY - canvas.offsetY) * ratio,
      });
    }
  };

  // --- Node mouse handlers ---

  const handleNodeMouseDown = (e: React.MouseEvent, nodeId: string) => {
    e.stopPropagation();
    if (contextMenu) {
      setContextMenu(null);
      return;
    }
    setSelectedNodeId(nodeId);
    setSelectedConnectionId(null);
    const node = nodes.find((n) => n.id === nodeId);
    if (node) {
      const pos = screenToCanvas(e.clientX, e.clientY);
      setDragOffset({ x: pos.x - node.position_x, y: pos.y - node.position_y });
      setIsDragging(true);
      setDragNodeId(nodeId);
    }
  };

  const handleNodeContextMenu = (e: React.MouseEvent, nodeId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedNodeId(nodeId);
    setContextMenu({ x: e.clientX, y: e.clientY, nodeId });
  };

  // --- Port handlers ---

  const handleOutputPortMouseDown = (e: React.MouseEvent, nodeId: string) => {
    e.stopPropagation();
    e.preventDefault();
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;
    const port = getOutputPortPos(node);
    setDragConnection({
      sourceNodeId: nodeId,
      mouseX: port.x,
      mouseY: port.y,
    });
  };

  const handleInputPortMouseUp = (e: React.MouseEvent, nodeId: string) => {
    e.stopPropagation();
    if (!dragConnection || dragConnection.sourceNodeId === nodeId) return;
    const exists = connections.some(
      (c) =>
        c.source_node_id === dragConnection.sourceNodeId &&
        c.target_node_id === nodeId,
    );
    if (!exists) {
      const newConn: EditorConnection = {
        id: generateConnectionId(),
        source_node_id: dragConnection.sourceNodeId,
        source_port_id: 'output',
        target_node_id: nodeId,
        target_port_id: 'input',
        condition: '',
        label: '',
      };
      setConnections((prev) => [...prev, newConn]);
      setHasChanges(true);
    }
    setDragConnection(null);
  };

  // --- Zoom controls ---

  const handleZoom = (delta: number) => {
    setCanvas((prev) => {
      const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev.zoom + delta));
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return { ...prev, zoom: newZoom };
      const cx = rect.width / 2;
      const cy = rect.height / 2;
      const ratio = newZoom / prev.zoom;
      return {
        zoom: newZoom,
        offsetX: cx - (cx - prev.offsetX) * ratio,
        offsetY: cy - (cy - prev.offsetY) * ratio,
      };
    });
  };

  // --- Node CRUD ---

  const addNode = (nodeType: any, posX?: number, posY?: number) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    let x = posX ?? 200 + Math.random() * 200;
    let y = posY ?? 100 + Math.random() * 200;
    if (!posX && rect) {
      const center = screenToCanvas(rect.left + rect.width / 2, rect.top + rect.height / 2);
      x = center.x - NODE_WIDTH / 2 + (Math.random() - 0.5) * 100;
      y = center.y - NODE_HEIGHT / 2 + (Math.random() - 0.5) * 60;
    }

    const open = openNodePosition(nodes, x, y);
    const newNode: EditorNode = {
      id: generateId(),
      name: nodeType.name || 'New Node',
      node_type_slug: nodeType.slug,
      position_x: Math.round(open.x / (GRID_SIZE / 2)) * (GRID_SIZE / 2),
      position_y: Math.round(open.y / (GRID_SIZE / 2)) * (GRID_SIZE / 2),
      config: {},
      is_disabled: false,
      notes: '',
      credential_id: '',
      _category: nodeType.category || '',
      _color: nodeType.color || getCategoryColor(nodeType.category),
      _icon_url: nodeType.icon_url || '',
    };
    setNodes((prev) => [...prev, newNode]);
    setSelectedNodeId(newNode.id);
    setHasChanges(true);
  };

  const addNodeFromQuickSearch = (nodeType: any) => {
    if (!quickSearch) return;
    const pos = screenToCanvas(
      (canvasRef.current?.getBoundingClientRect()?.left || 0) + quickSearch.x,
      (canvasRef.current?.getBoundingClientRect()?.top || 0) + quickSearch.y,
    );
    addNode(nodeType, pos.x - NODE_WIDTH / 2, pos.y - NODE_HEIGHT / 2);
    setQuickSearch(null);
    setQuickSearchQuery('');
  };

  const duplicateNode = (nodeId: string) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;
    const open = openNodePosition(nodes, node.position_x + NODE_WIDTH + NODE_GAP_X, node.position_y);
    const newNode: EditorNode = {
      ...node,
      id: generateId(),
      name: node.name + ' (copy)',
      position_x: open.x,
      position_y: open.y,
    };
    setNodes((prev) => [...prev, newNode]);
    setSelectedNodeId(newNode.id);
    setHasChanges(true);
  };

  const deleteNode = (nodeId: string) => {
    setNodes((prev) => prev.filter((n) => n.id !== nodeId));
    setConnections((prev) =>
      prev.filter((c) => c.source_node_id !== nodeId && c.target_node_id !== nodeId),
    );
    if (selectedNodeId === nodeId) setSelectedNodeId(null);
    setHasChanges(true);
  };

  const updateNodeField = (nodeId: string, field: string, value: any) => {
    setNodes((prev) =>
      prev.map((n) => (n.id === nodeId ? { ...n, [field]: value } : n)),
    );
    setHasChanges(true);
  };

  const deleteConnection = (connId: string) => {
    setConnections((prev) => prev.filter((c) => c.id !== connId));
    if (selectedConnectionId === connId) setSelectedConnectionId(null);
    setHasChanges(true);
  };

  // --- Save ---

  const handleSave = useCallback(async () => {
    if (!id) return;
    setIsSaving(true);
    try {
      const { SaveDraft } = await import('@core/api/endpoints/workflow/save_draft');
      await SaveDraft(id, {
        nodes: nodes.map(({ _selected, _category, _color, _icon_url, ...n }) => n) as any,
        connections,
        canvas_settings: {
          zoom: canvas.zoom,
          offset_x: canvas.offsetX,
          offset_y: canvas.offsetY,
        },
      });
      setHasChanges(false);
    } catch {
      // Error handled by store
    } finally {
      setIsSaving(false);
    }
  }, [id, nodes, connections, canvas, scope.productId, store]);

  // --- EVENT trigger config ---

  const openEventBrowser = () => {
    setEventBrowserOpen(true);
    setSelectedEventName(pipeline?.trigger_event || '');
    setSelectedEventDetail(null);
    if (!eventDescriptorsLoaded) {
      store.ListEventDescriptors?.()
        ?.then((res: any) => setEventDescriptors(res?.items?.items || res?.items || []))
        ?.catch(() => {})
        ?.finally(() => setEventDescriptorsLoaded(true));
    }
  };

  const previewEvent = async (eventName: string) => {
    setSelectedEventName(eventName);
    try {
      const res = await store.GetEventDescriptor?.(eventName);
      setSelectedEventDetail((res as any)?.event_descriptor ?? res ?? null);
    } catch {
      setSelectedEventDetail(null);
    }
  };

  const saveTriggerEvent = async () => {
    if (!id || !pipeline) return;
    setSavingTriggerEvent(true);
    try {
      await store.UpdatePipeline?.(id, { pipeline: { ...pipeline, trigger_event: selectedEventName } });
      setPipeline((prev: any) => ({ ...prev, trigger_event: selectedEventName }));
      setEventBrowserOpen(false);
    } catch {
      // Error handled by store
    } finally {
      setSavingTriggerEvent(false);
    }
  };

  // --- Keyboard shortcuts ---

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault();
        setSpaceHeld(true);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) {
        if (selectedNodeId) {
          deleteNode(selectedNodeId);
        }
        if (selectedConnectionId) {
          deleteConnection(selectedConnectionId);
        }
      }
      if (e.key === 'Escape') {
        setSelectedNodeId(null);
        setSelectedConnectionId(null);
        setDragConnection(null);
        setContextMenu(null);
        setQuickSearch(null);
        setQuickSearchQuery('');
        setShowShortcuts(false);
      }
      if (e.key === '?' && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) {
        setShowShortcuts((v) => !v);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'd' && selectedNodeId) {
        e.preventDefault();
        duplicateNode(selectedNodeId);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setSpaceHeld(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [selectedNodeId, selectedConnectionId, handleSave]);

  // --- Connection path computation for rendering ---

  const getConnPath = useCallback(
    (conn: EditorConnection) => {
      const sourceNode = nodes.find((n) => n.id === conn.source_node_id);
      const targetNode = nodes.find((n) => n.id === conn.target_node_id);
      if (!sourceNode || !targetNode) return '';
      const from = getOutputPortPos(sourceNode);
      const to = getInputPortPos(targetNode);
      return getConnectionPath(from.x, from.y, to.x, to.y);
    },
    [nodes, getOutputPortPos, getInputPortPos],
  );

  const getConnMidpoint = useCallback(
    (conn: EditorConnection) => {
      const sourceNode = nodes.find((n) => n.id === conn.source_node_id);
      const targetNode = nodes.find((n) => n.id === conn.target_node_id);
      if (!sourceNode || !targetNode) return { x: 0, y: 0 };
      const from = getOutputPortPos(sourceNode);
      const to = getInputPortPos(targetNode);
      return getConnectionMidpoint(from.x, from.y, to.x, to.y);
    },
    [nodes, getOutputPortPos, getInputPortPos],
  );

  // --- Toggle category collapse ---

  const toggleCategory = (cat: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  // --- Context menu actions ---

  const contextMenuActions = useMemo(() => {
    if (!contextMenu) return [];
    const node = nodes.find((n) => n.id === contextMenu.nodeId);
    if (!node) return [];
    return [
      {
        label: node.is_disabled ? t('workflow.enable') : t('workflow.disable'),
        icon: ToggleIcon,
        action: () => updateNodeField(contextMenu.nodeId, 'is_disabled', !node.is_disabled),
      },
      {
        label: t('workflow.duplicate'),
        icon: DuplicateIcon,
        action: () => duplicateNode(contextMenu.nodeId),
      },
      {
        label: t('workflow.addNote'),
        icon: NoteIcon,
        action: () => {
          setSelectedNodeId(contextMenu.nodeId);
        },
      },
      {
        label: t('workflow.copyNodeId'),
        icon: CopyIcon,
        action: () => {
          navigator.clipboard?.writeText(contextMenu.nodeId);
        },
      },
      { separator: true } as any,
      {
        label: t('workflow.deleteNode'),
        icon: TrashIcon,
        action: () => deleteNode(contextMenu.nodeId),
        danger: true,
      },
    ];
  }, [contextMenu, nodes, t]);

  // --- Render ---

  const cursorClass = spaceHeld
    ? 'cursor-grab'
    : isPanning
      ? 'cursor-grabbing'
      : dragConnection
        ? 'cursor-crosshair'
        : 'cursor-default';

  if (!accessReady) return <div className="p-6 text-sm text-muted-foreground">{t('workflow.loading')}</div>;
  if (!canEdit) return <div className="p-6"><Alert variant="warning" title={t('workflow.noEditAccess')}><Link to={`/workflows/${id}`} className="text-primary hover:underline">{t('common.backToList')}</Link></Alert></div>;

  return (
    <div className={`flex flex-col select-none ${paneSurface}`}>
      {/* ===== Toolbar ===== */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-background border-b border-border flex-shrink-0 h-12">
        {/* Left: Back + Name + Unsaved badge */}
        <div className="flex items-center gap-2 min-w-0">
          <Link
            to={`/workflows/${id}`}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </Link>
          <h2 className="font-semibold text-foreground truncate max-w-[200px] text-sm">
            {pipeline?.name || t('workflow.editor')}
          </h2>
          {hasChanges && (
            <Badge variant="warning" size="sm" className="flex-shrink-0">
              {t('workflow.unsaved')}
            </Badge>
          )}
        </div>

        {/* Center: Zoom controls */}
        <div className="flex items-center gap-1 bg-muted/60 rounded-lg px-1 py-0.5">
          <button
            onClick={() => fitToView()}
            className="p-1.5 rounded hover:bg-background transition-colors text-muted-foreground hover:text-foreground"
            title="Fit to view"
          >
            <FitIcon className="h-3.5 w-3.5" />
          </button>
          <div className="w-px h-4 bg-border mx-0.5" />
          <button
            onClick={() => handleZoom(-0.1)}
            className="p-1.5 rounded hover:bg-background transition-colors text-muted-foreground hover:text-foreground"
            title="Zoom out"
          >
            <ZoomOutIcon className="h-3.5 w-3.5" />
          </button>
          <span className="text-[11px] font-mono w-10 text-center text-muted-foreground tabular-nums">
            {Math.round(canvas.zoom * 100)}%
          </span>
          <button
            onClick={() => handleZoom(0.1)}
            className="p-1.5 rounded hover:bg-background transition-colors text-muted-foreground hover:text-foreground"
            title="Zoom in"
          >
            <ZoomInIcon className="h-3.5 w-3.5" />
          </button>
          <div className="w-px h-4 bg-border mx-0.5" />
          <button
            onClick={() => setShowNodePanel((v) => !v)}
            className={`p-1.5 rounded transition-colors ${showNodePanel ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground hover:bg-background'}`}
            title="Toggle node palette"
          >
            <PanelLeftIcon className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setShowShortcuts((v) => !v)}
            className="p-1.5 rounded hover:bg-background transition-colors text-muted-foreground hover:text-foreground"
            title="Keyboard shortcuts"
          >
            <KeyboardIcon className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Right: Run test + Save */}
        <div className="flex items-center gap-2">
          {pipeline?.trigger_type === 'TRIGGER_TYPE_EVENT' && (
            <Button variant="secondary" size="sm" onClick={openEventBrowser}>
              {pipeline?.trigger_event ? pipeline.trigger_event : t('workflow.eventTrigger.pickEvent')}
            </Button>
          )}
          <Button
            variant={showDebug ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setShowDebug((v) => !v)}
            aria-pressed={showDebug}
          >
            {t('workflow.debug.title')}
          </Button>
          <Button variant="secondary" size="sm">
            <PlayIcon className="h-3.5 w-3.5 mr-1.5" />
            {t('workflow.runTest')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            isLoading={isSaving}
          >
            <SaveIcon className="h-3.5 w-3.5 mr-1.5" />
            {t('workflow.saveDraft')}
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* ===== Node Palette (left panel) ===== */}
        {showNodePanel && (
          <div className="w-60 border-r border-border bg-background overflow-y-auto flex-shrink-0">
            <div className="p-3 pb-1">
              <div className="relative">
                <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder={t('workflow.searchNodes')}
                  value={nodeTypeSearch}
                  onChange={(e) => setNodeTypeSearch(e.target.value)}
                  className="w-full rounded-md border border-input bg-background pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                />
              </div>
            </div>
            <div className="px-2 pb-3">
              {(Object.entries(groupedNodeTypes) as [string, any[]][]).map(([category, types]) => {
                const catInfo = nodeTypeCategoryMap[category];
                const isCollapsed = collapsedCategories.has(category);
                const catColor = catInfo?.color || '#6b7280';
                return (
                  <div key={category} className="mt-1">
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-semibold uppercase text-muted-foreground hover:bg-muted transition-colors"
                    >
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: catColor }}
                      />
                      <span className="flex-1 text-left truncate">
                        {catInfo ? t(catInfo.labelKey) : category.replace('NODE_TYPE_CATEGORY_', '')}
                      </span>
                      <span className="text-[10px] text-muted-foreground/60 mr-1">{(types as any[]).length}</span>
                      {isCollapsed ? (
                        <ChevronRightIcon className="h-3 w-3 flex-shrink-0" />
                      ) : (
                        <ChevronDownIcon className="h-3 w-3 flex-shrink-0" />
                      )}
                    </button>
                    {!isCollapsed && (
                      <div className="ml-1 space-y-0.5 mt-0.5">
                        {(types as any[]).map((nt: any) => (
                          <button
                            key={getEntityId(nt) || nt.slug}
                            onClick={() => addNode(nt)}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left hover:bg-muted transition-colors group"
                          >
                            <span
                              className="w-5 h-5 rounded flex-shrink-0 flex items-center justify-center text-white text-[9px] font-bold"
                              style={{ backgroundColor: nt.color || catColor }}
                            >
                              {(nt.name || '?')[0].toUpperCase()}
                            </span>
                            <span className="truncate text-foreground text-[13px]">{nt.name}</span>
                            {nt.is_trigger && (
                              <Badge variant="primary" size="sm" className="ml-auto text-[9px] px-1">
                                T
                              </Badge>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {filteredNodeTypes.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">
                  {t('workflow.noNodeTypes')}
                </p>
              )}
            </div>
          </div>
        )}

        {/* ===== Canvas ===== */}
        <div
          ref={canvasRef}
          className={`flex-1 relative overflow-hidden bg-muted/20 ${cursorClass}`}
          onMouseDown={handleCanvasMouseDown}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          onMouseLeave={handleCanvasMouseUp}
          onWheel={handleWheel}
          onDoubleClick={handleCanvasDoubleClick}
        >
          {/* Dot grid background */}
          <div
            className="canvas-grid absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `radial-gradient(circle, hsl(var(--border)) 1px, transparent 1px)`,
              backgroundSize: `${GRID_SIZE * canvas.zoom}px ${GRID_SIZE * canvas.zoom}px`,
              backgroundPosition: `${canvas.offsetX % (GRID_SIZE * canvas.zoom)}px ${canvas.offsetY % (GRID_SIZE * canvas.zoom)}px`,
            }}
          />

          {/* Connections SVG layer */}
          <svg
            ref={svgRef}
            className="absolute inset-0 w-full h-full overflow-visible pointer-events-none"
            style={{
              transform: `translate(${canvas.offsetX}px, ${canvas.offsetY}px) scale(${canvas.zoom})`,
              transformOrigin: '0 0',
            }}
          >
            <defs>
              <filter id="conn-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Rendered connections */}
            {connections.map((conn) => {
              const path = getConnPath(conn);
              if (!path) return null;
              const isSelected = selectedConnectionId === conn.id;
              const mid = getConnMidpoint(conn);
              return (
                <g key={conn.id}>
                  {/* Invisible fat hitbox for clicking */}
                  <path
                    d={path}
                    stroke="transparent"
                    strokeWidth={14}
                    fill="none"
                    className="pointer-events-auto cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedConnectionId(conn.id);
                      setSelectedNodeId(null);
                    }}
                  />
                  {/* Visible connection line */}
                  <path
                    d={path}
                    stroke={isSelected ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))'}
                    strokeWidth={isSelected ? 2.5 : 2}
                    fill="none"
                    strokeLinecap="round"
                    opacity={isSelected ? 1 : 0.5}
                    filter={isSelected ? 'url(#conn-glow)' : undefined}
                    className="pointer-events-none transition-opacity"
                  />
                  {/* Label */}
                  {conn.label && (
                    <text
                      x={mid.x}
                      y={mid.y - 8}
                      textAnchor="middle"
                      className="text-[10px] pointer-events-none"
                      fill="hsl(var(--muted-foreground))"
                    >
                      {conn.label}
                    </text>
                  )}
                  {/* Delete button at midpoint when selected */}
                  {isSelected && (
                    <g
                      className="pointer-events-auto cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteConnection(conn.id);
                      }}
                    >
                      <circle cx={mid.x} cy={mid.y} r={9} fill="hsl(var(--destructive))" opacity={0.9} />
                      <line
                        x1={mid.x - 3.5}
                        y1={mid.y - 3.5}
                        x2={mid.x + 3.5}
                        y2={mid.y + 3.5}
                        stroke="white"
                        strokeWidth={1.5}
                        strokeLinecap="round"
                      />
                      <line
                        x1={mid.x + 3.5}
                        y1={mid.y - 3.5}
                        x2={mid.x - 3.5}
                        y2={mid.y + 3.5}
                        stroke="white"
                        strokeWidth={1.5}
                        strokeLinecap="round"
                      />
                    </g>
                  )}
                </g>
              );
            })}

            {/* Drag connection rubber-band line */}
            {dragConnection && (() => {
              const sourceNode = nodes.find((n) => n.id === dragConnection.sourceNodeId);
              if (!sourceNode) return null;
              const from = getOutputPortPos(sourceNode);
              const path = getConnectionPath(
                from.x,
                from.y,
                dragConnection.mouseX,
                dragConnection.mouseY,
              );
              return (
                <path
                  d={path}
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  fill="none"
                  strokeDasharray="6 3"
                  opacity={0.7}
                  className="pointer-events-none"
                />
              );
            })()}
          </svg>

          {/* Nodes layer */}
          <div
            className="absolute inset-0"
            style={{
              transform: `translate(${canvas.offsetX}px, ${canvas.offsetY}px) scale(${canvas.zoom})`,
              transformOrigin: '0 0',
            }}
          >
            {nodes.map((node) => {
              const isSelected = selectedNodeId === node.id;
              const color = node._color || '#6b7280';
              const isConnectTarget = dragConnection && dragConnection.sourceNodeId !== node.id;

              return (
                <div
                  key={node.id}
                  className="absolute group"
                  style={{
                    left: node.position_x,
                    top: node.position_y,
                    width: NODE_WIDTH,
                    height: NODE_HEIGHT,
                  }}
                  onMouseDown={(e) => handleNodeMouseDown(e, node.id)}
                  onContextMenu={(e) => handleNodeContextMenu(e, node.id)}
                >
                  {/* Node card */}
                  <div
                    className={`
                      w-full h-full rounded-xl bg-popover border overflow-hidden
                      transition-all duration-150
                      ${isSelected
                        ? 'shadow-lg border-transparent'
                        : 'shadow-sm border-border hover:shadow-md hover:border-border'}
                      ${node.is_disabled ? 'opacity-50' : ''}
                    `}
                    style={{
                      ...(isSelected
                        ? {
                            boxShadow: `0 0 0 2px ${color}40, 0 4px 12px rgba(0,0,0,0.1)`,
                            borderColor: color,
                          }
                        : {}),
                    }}
                  >
                    {/* Colored left border stripe */}
                    <div
                      className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl"
                      style={{ backgroundColor: color }}
                    />

                    {/* Disabled striped overlay */}
                    {node.is_disabled && (
                      <div
                        className="absolute inset-0 rounded-xl pointer-events-none z-10"
                        style={{
                          background: `repeating-linear-gradient(
                            -45deg,
                            transparent,
                            transparent 4px,
                            hsl(var(--muted)) 4px,
                            hsl(var(--muted)) 5px
                          )`,
                          opacity: 0.3,
                        }}
                      />
                    )}

                    {/* Header area */}
                    <div className="flex items-center gap-2 px-3 pl-4 pt-2.5 pb-1">
                      {/* Icon circle */}
                      <span
                        className="w-6 h-6 rounded-md flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                        style={{ backgroundColor: color }}
                      >
                        {(node.name || '?')[0].toUpperCase()}
                      </span>
                      <span className="text-sm font-medium text-foreground truncate flex-1 leading-tight">
                        {node.name}
                      </span>
                      {node.is_disabled && (
                        <span className="text-[9px] font-medium text-muted-foreground bg-muted rounded px-1 py-0.5">
                          OFF
                        </span>
                      )}
                    </div>

                    {/* Body */}
                    <div className="px-3 pl-4 pb-2">
                      <p className="text-[11px] text-muted-foreground font-mono truncate">
                        {node.node_type_slug}
                      </p>
                    </div>

                    {/* Notes indicator */}
                    {node.notes && (
                      <div className="absolute top-1.5 right-1.5">
                        <NoteIcon className="h-3 w-3 text-muted-foreground/50" />
                      </div>
                    )}
                  </div>

                  {/* Input port (LEFT side) */}
                  <div
                    className="absolute z-20"
                    style={{
                      left: -PORT_RADIUS - 1,
                      top: NODE_HEIGHT / 2 - PORT_HIT_RADIUS,
                      width: PORT_HIT_RADIUS * 2,
                      height: PORT_HIT_RADIUS * 2,
                    }}
                    onMouseUp={(e) => handleInputPortMouseUp(e, node.id)}
                    onMouseEnter={() => {
                      setHoveredPortNodeId(node.id);
                      setHoveredPortType('input');
                    }}
                    onMouseLeave={() => {
                      setHoveredPortNodeId(null);
                      setHoveredPortType(null);
                    }}
                  >
                    <div
                      className={`
                        absolute rounded-full border-2 transition-all duration-150
                        ${isConnectTarget && hoveredPortNodeId === node.id && hoveredPortType === 'input'
                          ? 'scale-150 border-primary bg-primary/20'
                          : isConnectTarget
                            ? 'border-primary/50 bg-background'
                            : 'border-border bg-background group-hover:border-muted-foreground'}
                      `}
                      style={{
                        width: PORT_RADIUS * 2,
                        height: PORT_RADIUS * 2,
                        left: PORT_HIT_RADIUS - PORT_RADIUS,
                        top: PORT_HIT_RADIUS - PORT_RADIUS,
                      }}
                    />
                  </div>

                  {/* Output port (RIGHT side) */}
                  <div
                    className="absolute z-20 cursor-crosshair"
                    style={{
                      right: -PORT_RADIUS - 1,
                      top: NODE_HEIGHT / 2 - PORT_HIT_RADIUS,
                      width: PORT_HIT_RADIUS * 2,
                      height: PORT_HIT_RADIUS * 2,
                    }}
                    onMouseDown={(e) => handleOutputPortMouseDown(e, node.id)}
                    onMouseEnter={() => {
                      setHoveredPortNodeId(node.id);
                      setHoveredPortType('output');
                    }}
                    onMouseLeave={() => {
                      setHoveredPortNodeId(null);
                      setHoveredPortType(null);
                    }}
                  >
                    <div
                      className={`
                        absolute rounded-full border-2 transition-all duration-150
                        ${hoveredPortNodeId === node.id && hoveredPortType === 'output'
                          ? 'scale-125 border-primary bg-primary/20'
                          : 'border-border bg-background group-hover:border-muted-foreground'}
                      `}
                      style={{
                        width: PORT_RADIUS * 2,
                        height: PORT_RADIUS * 2,
                        left: PORT_HIT_RADIUS - PORT_RADIUS,
                        top: PORT_HIT_RADIUS - PORT_RADIUS,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quick search popup (double-click canvas) */}
          {quickSearch && (
            <div
              className="absolute z-50 bg-popover border border-border rounded-lg shadow-xl overflow-hidden"
              style={{
                left: quickSearch.x,
                top: quickSearch.y,
                width: 240,
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="p-2 border-b border-border">
                <div className="relative">
                  <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <input
                    ref={quickSearchInputRef}
                    type="text"
                    value={quickSearchQuery}
                    onChange={(e) => setQuickSearchQuery(e.target.value)}
                    placeholder={t('workflow.addNode')}
                    className="w-full rounded-md border border-input bg-background pl-7 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setQuickSearch(null);
                        setQuickSearchQuery('');
                      }
                      if (e.key === 'Enter' && quickSearchFiltered.length > 0) {
                        addNodeFromQuickSearch(quickSearchFiltered[0]);
                      }
                    }}
                  />
                </div>
              </div>
              <div className="max-h-52 overflow-y-auto py-1">
                {quickSearchFiltered.map((nt: any) => {
                  const catColor = nt.color || getCategoryColor(nt.category);
                  return (
                    <button
                      key={getEntityId(nt) || nt.slug}
                      onClick={() => addNodeFromQuickSearch(nt)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-muted transition-colors"
                    >
                      <span
                        className="w-4 h-4 rounded flex-shrink-0 flex items-center justify-center text-white text-[8px] font-bold"
                        style={{ backgroundColor: catColor }}
                      >
                        {(nt.name || '?')[0].toUpperCase()}
                      </span>
                      <span className="truncate text-foreground">{nt.name}</span>
                      <span className="text-[10px] text-muted-foreground/60 ml-auto truncate max-w-[60px]">
                        {(nt.category || '').replace('NODE_TYPE_CATEGORY_', '').toLowerCase()}
                      </span>
                    </button>
                  );
                })}
                {quickSearchFiltered.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-3">
                    {t('workflow.noNodeTypes')}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Context menu */}
          {contextMenu && (
            <div
              className="fixed z-50 bg-popover border border-border rounded-lg shadow-xl py-1 min-w-[180px]"
              style={{ left: contextMenu.x, top: contextMenu.y }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {contextMenuActions.map((item: any, i: number) =>
                item.separator ? (
                  <div key={i} className="my-1 border-t border-border" />
                ) : (
                  <button
                    key={i}
                    onClick={() => {
                      item.action();
                      setContextMenu(null);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-muted transition-colors ${item.danger ? 'text-destructive' : 'text-foreground'}`}
                  >
                    {item.icon && <item.icon className="h-3.5 w-3.5 flex-shrink-0" />}
                    <span>{item.label}</span>
                  </button>
                ),
              )}
            </div>
          )}

          {/* Keyboard shortcuts overlay */}
          {showShortcuts && (
            <div className="absolute inset-0 flex items-center justify-center z-50 bg-background/60 backdrop-blur-sm">
              <div
                className="bg-popover border border-border rounded-xl shadow-2xl p-6 max-w-sm w-full"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-foreground">{t('workflow.keyboardShortcuts')}</h3>
                  <button
                    onClick={() => setShowShortcuts(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                </div>
                <div className="space-y-2 text-sm">
                  {([
                    ['Ctrl/Cmd + S', t('workflow.saveDraft')],
                    ['Delete / Backspace', t('workflow.deleteNode')],
                    ['Ctrl/Cmd + D', t('workflow.duplicate')],
                    ['Escape', t('workflow.deselect')],
                    ['Ctrl/Cmd + scroll', t('workflow.zoom')],
                    ['Double-click canvas', t('workflow.addNode')],
                    ['Right-click node', t('workflow.contextMenu')],
                    ['?', t('workflow.keyboardShortcuts')],
                  ] as [string, string][]).map(([key, desc]) => (
                    <div key={key} className="flex items-center justify-between gap-4">
                      <span className="text-muted-foreground">{desc}</span>
                      <kbd className="px-2 py-0.5 bg-muted rounded text-[11px] font-mono text-foreground border border-border">
                        {key}
                      </kbd>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Empty state */}
          {graphLoading && <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-sm text-muted-foreground">{t('workflow.loading')}</div>}
          {graphError && <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-sm text-destructive">{t('workflow.loadError')}</div>}
          {!graphLoading && !graphError && nodes.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-muted-foreground/40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                    <path d="M12 4v16m8-8H4" strokeLinecap="round" />
                  </svg>
                </div>
                <p className="text-muted-foreground font-medium mb-1">
                  {t('workflow.emptyCanvas')}
                </p>
                <p className="text-sm text-muted-foreground/70">
                  {t('workflow.emptyCanvasHint')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ===== Debug Panel (right side, minimal) ===== */}
        {showDebug && id && (
          <DebugPanel
            pipelineId={id}
            selectedNodeId={selectedNodeId}
            store={store}
            t={t}
            onClose={() => setShowDebug(false)}
          />
        )}

        {/* ===== Properties Panel (right side) ===== */}
        {selectedNode && (
          <div className="w-80 border-l border-border bg-background overflow-y-auto flex-shrink-0">
            <div className="p-4 space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-6 h-6 rounded-md flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                    style={{ backgroundColor: selectedNode._color || '#6b7280' }}
                  >
                    {(selectedNode.name || '?')[0].toUpperCase()}
                  </span>
                  <h3 className="font-semibold text-foreground text-sm truncate">
                    {t('workflow.nodeProperties')}
                  </h3>
                </div>
                <button
                  onClick={() => setSelectedNodeId(null)}
                  className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-3">
                {/* Node name */}
                <Input
                  label={t('workflow.field.name')}
                  value={selectedNode.name}
                  onChange={(e) => updateNodeField(selectedNode.id, 'name', e.target.value)}
                />

                {/* Node type display */}
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">
                    {t('workflow.nodeType')}
                  </label>
                  <p className="text-sm text-muted-foreground font-mono bg-muted rounded-md px-2.5 py-1.5 truncate">
                    {selectedNode.node_type_slug}
                  </p>
                </div>

                {/* Config fields via ConfigFieldEditor */}
                <ConfigFieldEditor
                  configFields={
                    nodeTypes.find((nt: any) => nt.slug === selectedNode.node_type_slug)
                      ?.config_fields || []
                  }
                  config={selectedNode.config || {}}
                  onChange={(newConfig) =>
                    updateNodeField(selectedNode.id, 'config', newConfig)
                  }
                />

                {/* Credential ID */}
                {selectedNode.credential_id !== undefined && (
                  <Input
                    label={t('workflow.credentialId')}
                    value={selectedNode.credential_id}
                    onChange={(e) =>
                      updateNodeField(selectedNode.id, 'credential_id', e.target.value)
                    }
                    placeholder={t('workflow.credentialIdPlaceholder')}
                  />
                )}

                {/* Disable toggle */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedNode.is_disabled}
                    onChange={(e) =>
                      updateNodeField(selectedNode.id, 'is_disabled', e.target.checked)
                    }
                    className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">{t('workflow.disabled')}</span>
                </label>

                {/* Notes section */}
                <Textarea
                  label={t('workflow.field.notes')}
                  value={selectedNode.notes}
                  onChange={(e) =>
                    updateNodeField(selectedNode.id, 'notes', e.target.value)
                  }
                  rows={2}
                  placeholder={t('workflow.notesPlaceholder')}
                />
              </div>

              {/* Delete button */}
              <div className="pt-3 border-t border-border">
                <Button
                  variant="danger"
                  size="sm"
                  className="w-full"
                  onClick={() => deleteNode(selectedNode.id)}
                >
                  <TrashIcon className="h-3.5 w-3.5 mr-1.5" />
                  {t('workflow.deleteNode')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error toast */}
      {store.error ? (
        <div className="absolute bottom-4 right-4 max-w-sm z-50">
          <ErrorAlert
            error={store.error}
            dismissible
            onDismiss={() => store.clearError()}
          />
        </div>
      ) : null}

      {/* EVENT trigger config — ListEventDescriptors + GetEventDescriptor */}
      <Modal
        open={eventBrowserOpen}
        onClose={() => setEventBrowserOpen(false)}
        title={t('workflow.eventTrigger.title')}
        widthClass="max-w-2xl"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEventBrowserOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={saveTriggerEvent} isLoading={savingTriggerEvent} disabled={!selectedEventName}>{t('workflow.eventTrigger.use')}</Button>
          </>
        }
      >
        <div className="flex gap-4">
          <div className="w-1/2 max-h-72 overflow-y-auto space-y-1 border-r border-border pr-3">
            {!eventDescriptorsLoaded ? (
              <p className="text-sm text-muted-foreground">{t('workflow.loading')}</p>
            ) : eventDescriptors.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('workflow.eventTrigger.empty')}</p>
            ) : (
              eventDescriptors.map((ev: any) => (
                <button
                  key={ev.event_name}
                  onClick={() => previewEvent(ev.event_name)}
                  className={`w-full text-left px-2 py-1.5 rounded-md text-sm transition-colors ${
                    selectedEventName === ev.event_name ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-foreground'
                  }`}
                >
                  <span className="block font-medium">{ev.display_name || ev.event_name}</span>
                  <span className="block text-xs text-muted-foreground font-mono">{ev.event_name}</span>
                </button>
              ))
            )}
          </div>
          <div className="w-1/2 space-y-2">
            {selectedEventDetail ? (
              <>
                <p className="text-sm text-foreground">{selectedEventDetail.description}</p>
                {selectedEventDetail.category && (
                  <Badge variant="secondary" size="sm">{selectedEventDetail.category}</Badge>
                )}
                {selectedEventDetail.payload_schema && (
                  <pre className="bg-muted rounded-md p-2 text-xs font-mono overflow-x-auto max-h-40">
                    {selectedEventDetail.payload_schema}
                  </pre>
                )}
              </>
            ) : selectedEventName ? (
              <p className="text-sm text-muted-foreground font-mono">{selectedEventName}</p>
            ) : (
              <p className="text-sm text-muted-foreground">{t('workflow.eventTrigger.selectHint')}</p>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
});

export default WorkflowEditPage;
